import { useListings } from "@/contexts/listings-provider";
import { useTeams } from "@/contexts/teams-provider";
import { useUser } from "@/contexts/user-provider";
import { useUsers } from "@/contexts/users-provider";
import { useApi } from "@/hooks/useApi";
import { useArePeopleVisible } from "@/hooks/useArePeopleVisible";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { CaretSortIcon, CheckIcon } from "@radix-ui/react-icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@radix-ui/react-popover";
import { ListingDTO, ListingOwnerType, ListingType } from "@shaarp/common";
import { SparklesIcon } from "lucide-react";
import { FormEvent, useEffect, useState } from "react";
import { ButtonLoading } from "../button-loading";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "../ui/command";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Separator } from "../ui/separator";
import { TextareaAutosize } from "../ui/textarea-autosize";
import { useToast, useToastError } from "../ui/use-toast";

type CreateListingFormVariant = "dialog" | "sheet";

type CreateListingFormProps = {
  readonly defaultOwnerType?: ListingOwnerType;
  readonly defaultListingType?: ListingType;
  readonly defaultAssigneeId?: string;
  readonly searchQuery?: string;
  /**
   * Items the list is created with. They travel with the creation request so an
   * interrupted browser cannot leave a titled but empty list behind.
   */
  readonly companiesIds?: readonly string[];
  readonly employeesIds?: readonly string[];
  readonly variant?: CreateListingFormVariant;
  readonly onCancel: () => void;
  readonly onCreated: (listing: ListingDTO) => void;
};

export default function CreateListingForm({
  defaultOwnerType,
  defaultListingType,
  defaultAssigneeId,
  searchQuery,
  companiesIds,
  employeesIds,
  variant = "dialog",
  onCancel,
  onCreated,
}: CreateListingFormProps) {
  const api = useApi();
  const { toast } = useToast();
  const toastError = useToastError();
  const { t } = useTranslation();

  const { apiFetchListings } = useListings();
  const { isAdmin, canViewCollaborators, teams, teamSelected } = useTeams();
  const { teamUser } = useUser();
  const { users, apiFetchUsers } = useUsers();
  // A team whose contact switch is off sees no key contacts (#757): it only ever
  // creates a list of companies, so it is not asked which kind it wants.
  const arePeopleVisible = useArePeopleVisible();

  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [listingType, setListingType] = useState<ListingType>(
    defaultListingType || "companies",
  );
  const [ownerType, setOwnerType] = useState<ListingOwnerType>(
    defaultOwnerType || "user",
  );
  const [showTeamsDisabled, setShowTeamsDisabled] = useState(false);
  const [teamsSelected, setTeamsSelected] = useState<string[]>([]);
  const [assigneeId, setAssigneeId] = useState<string | null>(
    defaultAssigneeId || null,
  );
  const [openTeams, setOpenTeams] = useState(false);
  const [openAssignee, setOpenAssignee] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (canViewCollaborators) {
      apiFetchUsers();
    }
  }, [canViewCollaborators]);

  const assignableUsers = users.filter((u) => u.id !== teamUser?.id);
  const selectedAssignee = assignableUsers.find((u) => u.id === assigneeId);
  const showAssigneeField = canViewCollaborators && ownerType === "user";

  useEffect(() => {
    if (!searchQuery) return;

    const generateMetadata = async () => {
      setIsGenerating(true);
      try {
        const { data } = await api.post<{ title: string; description: string }>(
          `/listing/generate-metadata`,
          { searchQuery },
        );
        setTitle(data.title);
        setDescription(data.description);
      } catch {
        // Silently fail — user can still fill in manually
      } finally {
        setIsGenerating(false);
      }
    };

    generateMetadata();
  }, []);

  const filterTeams = (value: string, search: string) => {
    return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
  };

  const toggleTeam = (teamId: string) => {
    if (teamsSelected.includes(teamId)) {
      setTeamsSelected(teamsSelected.filter((t) => t !== teamId));
    } else {
      setTeamsSelected([...teamsSelected, teamId]);
    }
  };

  useEffect(() => {
    if (teamSelected?.id) {
      setTeamsSelected([...teamsSelected, teamSelected.id]);
    }
  }, [teamSelected]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const { data } = await api.post<ListingDTO>(`/listing`, {
        listing: {
          title,
          description,
          listingType,
          ownerType,
          allowedTeams: teamsSelected,
        },
        companiesIds,
        employeesIds,
      });
      if (showAssigneeField && assigneeId) {
        await api.post(`/listing/${data.id}/assignees`, {
          userIds: [assigneeId],
        });
      }
      toast({
        variant: "default",
        title: t("listing.dialog.create.success.title"),
        description: t("listing.dialog.create.success.description"),
      });
      await apiFetchListings({ force: true });
      onCreated(data);
    } catch (error) {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  const isSheet = variant === "sheet";

  return (
    <form onSubmit={onSubmit} className={cn(isSheet && "flex flex-col h-full")}>
      {!isSheet && <Separator className="my-4" />}
      <div
        className={cn(
          isSheet
            ? "flex-1 min-h-0 overflow-auto px-5 pt-2 pb-4"
            : "overflow-auto max-h-[70vh] px-3",
        )}
      >
        <div className="space-y-5">
          <div className="grid w-full items-center gap-1.5">
            <Label htmlFor="title">
              {t("listing.dialog.create.fields.title.label")}
            </Label>
            <p className="text-xs text-gray-500">
              {t("listing.dialog.create.fields.title.helper")}
            </p>
            <div className="relative">
              <Input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                disabled={isGenerating}
                placeholder={
                  isGenerating
                    ? t("listing.dialog.create.fields.title.generating")
                    : undefined
                }
              />
              {isGenerating && (
                <SparklesIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-pulse" />
              )}
            </div>
          </div>
          <div className="grid w-full items-center gap-1.5">
            <Label htmlFor="description">
              {t("listing.dialog.create.fields.description.label")}
            </Label>
            <p className="text-xs text-gray-500">
              {t("listing.dialog.create.fields.description.helper")}
            </p>
            <div className="relative">
              <TextareaAutosize
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isGenerating}
                placeholder={
                  isGenerating
                    ? t("listing.dialog.create.fields.description.generating")
                    : undefined
                }
              />
              {isGenerating && (
                <SparklesIcon className="absolute right-3 top-3 w-4 h-4 text-muted-foreground animate-pulse" />
              )}
            </div>
          </div>
          {!defaultListingType && arePeopleVisible && (
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="listingType">
                {t("listing.dialog.create.fields.listingType.label")}
              </Label>
              <p className="text-xs text-gray-500">
                {t("listing.dialog.create.fields.listingType.helper")}
              </p>
              <Select
                value={listingType}
                onValueChange={(newValue) =>
                  setListingType(newValue as ListingType)
                }
              >
                <SelectTrigger>
                  <SelectValue defaultValue={listingType} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="companies">
                      {t(
                        "listing.dialog.create.fields.listingType.options.companies",
                      )}
                    </SelectItem>
                    <SelectItem value="employees">
                      {t(
                        "listing.dialog.create.fields.listingType.options.employees",
                      )}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          )}
          {isAdmin && (
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="ownerType">
                {t("listing.dialog.create.fields.ownerType.label")}
              </Label>
              <p className="text-xs text-gray-500">
                {t("listing.dialog.create.fields.ownerType.helper")}
              </p>
              <Select
                value={ownerType}
                onValueChange={(newValue) =>
                  setOwnerType(newValue as ListingOwnerType)
                }
              >
                <SelectTrigger>
                  <SelectValue defaultValue={ownerType} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="user">
                      {t(
                        "listing.dialog.create.fields.ownerType.options.user",
                      )}
                    </SelectItem>
                    <SelectItem value="shaarp">
                      {t(
                        "listing.dialog.create.fields.ownerType.options.shaarp",
                      )}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          )}
          {showAssigneeField && (
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="assignee">
                {t("listing.dialog.create.fields.assignee.label")}
              </Label>
              <p className="text-xs text-gray-500">
                {t("listing.dialog.create.fields.assignee.helper")}
              </p>
              <Popover
                open={openAssignee}
                onOpenChange={setOpenAssignee}
                modal={true}
              >
                <PopoverTrigger asChild>
                  <Button
                    id="assignee"
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={openAssignee}
                    className={cn("justify-between w-full")}
                  >
                    {selectedAssignee ? (
                      <span className="flex gap-2 items-center min-w-0">
                        <Avatar className="w-5 h-5">
                          <AvatarImage src={selectedAssignee.imageUrl} />
                          <AvatarFallback className="text-xs">
                            {selectedAssignee.fullName?.charAt(0) || "U"}
                          </AvatarFallback>
                        </Avatar>
                        <span className="truncate">
                          {selectedAssignee.fullName}
                        </span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">
                        {t("listing.dialog.create.fields.assignee.select")}
                      </span>
                    )}
                    <CaretSortIcon className="ml-2 w-4 h-4 opacity-50 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="z-50 p-0 w-[var(--radix-popover-trigger-width)] rounded-md border shadow-xs bg-background"
                  align="start"
                >
                  <Command>
                    <CommandInput
                      placeholder={t(
                        "listing.dialog.create.fields.assignee.search",
                      )}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {t("listing.dialog.create.fields.assignee.empty")}
                      </CommandEmpty>
                      <CommandGroup>
                        {assignableUsers.map((user) => (
                          <CommandItem
                            key={user.id}
                            value={user.fullName}
                            onSelect={() => {
                              setAssigneeId((prev) =>
                                prev === user.id ? null : user.id,
                              );
                              setOpenAssignee(false);
                            }}
                            className="flex gap-2 items-center cursor-pointer"
                          >
                            <CheckIcon
                              className={cn(
                                "w-4 h-4 text-primary opacity-0",
                                assigneeId === user.id && "opacity-100",
                              )}
                            />
                            <Avatar className="w-6 h-6">
                              <AvatarImage src={user.imageUrl} />
                              <AvatarFallback className="text-xs">
                                {user.fullName?.charAt(0) || "U"}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col">
                              <span className="text-sm font-medium">
                                {user.fullName}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {user.email}
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}
          {ownerType === "shaarp" && (
            <div className="grid w-full items-center gap-1.5">
              <Popover open={openTeams} onOpenChange={setOpenTeams} modal={true}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={openTeams}
                    aria-label="Select a team"
                    className={cn("justify-between w-[220px]")}
                  >
                    {teamsSelected.length > 0
                      ? t("listing.dialog.create.fields.teams.selected", {
                          count: teamsSelected.length,
                        })
                      : t("listing.dialog.create.fields.teams.select")}
                    <CaretSortIcon className="ml-auto w-4 h-4 opacity-50 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0">
                  <Command filter={filterTeams}>
                    {teams.length > 5 && (
                      <div className="relative">
                        <CommandInput
                          placeholder={t(
                            "listing.dialog.create.fields.teams.search",
                          )}
                          className="flex-1"
                          onValueChange={(value) =>
                            setShowTeamsDisabled(!!value)
                          }
                        />
                      </div>
                    )}
                    <CommandSeparator />
                    <CommandList>
                      <CommandGroup key="teamsAdmin">
                        {teams
                          .filter(
                            (team) =>
                              team.status === "enabled" || showTeamsDisabled,
                          )
                          .map((team) => (
                            <CommandItem
                              key={team.id}
                              disabled={team.id === teamSelected?.id}
                              onSelect={() => {
                                toggleTeam(team.id);
                              }}
                            >
                              <span>
                                <CheckIcon
                                  className={cn(
                                    "inline-block h-4 w-4 mr-2 text-primary opacity-0",
                                    teamsSelected.includes(team.id) &&
                                      "opacity-100",
                                  )}
                                />
                                {team.name}
                                {team.id === teamSelected?.id && (
                                  <Badge variant="outline" className="ml-2">
                                    Propriétaire
                                  </Badge>
                                )}
                              </span>
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>
      </div>
      {isSheet ? (
        <div
          className="shrink-0 border-t bg-background px-5 pt-3 pb-2 flex flex-col gap-1"
          style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}
        >
          <ButtonLoading
            type="submit"
            isLoading={isLoading}
            className="w-full h-12 rounded-xl text-base"
          >
            {t("listing.dialog.create.buttons.create")}
          </ButtonLoading>
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={onCancel}
          >
            {t("listing.dialog.create.buttons.close")}
          </Button>
        </div>
      ) : (
        <>
          <Separator className="my-4" />
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
            <Button type="button" variant="ghost" onClick={onCancel}>
              {t("listing.dialog.create.buttons.close")}
            </Button>
            <ButtonLoading type="submit" isLoading={isLoading}>
              {t("listing.dialog.create.buttons.create")}
            </ButtonLoading>
          </div>
        </>
      )}
    </form>
  );
}
